// CRAP is inflated only when the CI fallow coverage step times out (0% scoring, see
// .github/workflows/fallow.yml); these branchy fns are fully unit-tested. Drop when fixed.
// fallow-ignore-file complexity
// @plasser/ui — LayeredModalHost: an in-context surface stack.
// A navigation stack of overlays (modal / side-sheet / bottom-sheet). Each layer
// is opened imperatively and returns a Promise that resolves with the layer's
// result (or undefined when dismissed). Esc closes the TOPMOST layer; focus is
// trapped in the topmost layer. On mobile every layer renders as a bottom-sheet,
// so the stack doubles as the mobile back-stack.
//
// TES-2105: this host is frequently nested INSIDE a Radix modal Dialog (e.g. a
// picker opened from a form dialog). A Radix modal Dialog's DismissableLayer sets
// `document.body.style.pointerEvents = "none"` while it is open and re-enables
// `pointer-events: auto` ONLY on its own registered layer node — a plain
// `createPortal(..., document.body)` sibling like this host's stack is NOT that
// node, so real pointer clicks silently stop hitting it (though JS-dispatched
// events still "work", which is why several e2e specs quietly worked around this
// with `row.dispatchEvent("click")` instead of a real `.click()`). Three things
// are needed to make this host a first-class citizen of an ambient Radix
// dismissable-layer stack, not just a visually-stacked-on-top orphan portal:
//   1. `pointerEvents: "auto"` on the portaled root — opts back in to real clicks
//      regardless of what an ancestor Dialog did to `body.style.pointerEvents`.
//   2. `DismissableLayerBranch` — Radix's own composition primitive for "this
//      subtree counts as INSIDE any active dismissable layer", so a pointerdown
//      here is never treated as an outside-click by an ancestor Dialog (which
//      would otherwise instantly dismiss the ambient Dialog before the layer's
//      own click handler ever runs).
//   3. Each layer's panel is a Radix `FocusScope` (same primitive Dialog itself
//      uses), not a hand-rolled Tab-trap. A modal Dialog's FocusScope installs a
//      global `focusin` listener that snaps focus BACK inside its own container
//      whenever anything OUTSIDE it gains focus — including this host's portal,
//      since it is a DOM sibling, not a descendant. Mounting our own `FocusScope`
//      registers into Radix's shared focus-scope stack, which auto-pauses the
//      previously-active scope (the ambient Dialog's, or a lower layer's own) —
//      the same mechanism Radix uses to nest Dialog-in-Dialog correctly. Without
//      this, autoFocus into the layer gets yanked back to the trigger button, so
//      keystrokes (e.g. a space while typing a search query) land on that BUTTON
//      instead of the search input and re-activate it — silently stacking a NEW
//      layer per space character instead of ever filtering the list.

import * as React from "react";
import { createPortal } from "react-dom";
import { Branch as DismissableLayerBranch } from "@radix-ui/react-dismissable-layer";
import { FocusScope } from "@radix-ui/react-focus-scope";

import { cn } from "../lib/utils";
import { useIsMobile } from "./useIsMobile";

export type SurfaceKind = "modal" | "sheet" | "bottom-sheet";

export interface SurfaceOptions {
  kind?: SurfaceKind;
  /** Accessible title (also used as aria-label). */
  title?: string;
  /** Width for sheet/modal on desktop. */
  className?: string;
  /** Disable Esc / backdrop dismissal (e.g. a dirty form). */
  dismissable?: boolean;
}

/** API handed to a layer's render fn: close it (optionally with a result). */
export interface LayerApi<T> {
  close: (result?: T) => void;
}

interface InternalLayer {
  id: number;
  kind: SurfaceKind;
  title?: string;
  className?: string;
  dismissable: boolean;
  render: (api: LayerApi<unknown>) => React.ReactNode;
  resolve: (result: unknown) => void;
}

interface LayeredModalContextValue {
  open<T = unknown>(
    render: (api: LayerApi<T>) => React.ReactNode,
    options?: SurfaceOptions,
  ): Promise<T | undefined>;
  /** Close the topmost layer. */
  closeTop: (result?: unknown) => void;
  /** Number of open layers (stack depth). */
  depth: number;
}

const LayeredModalContext = React.createContext<LayeredModalContextValue | null>(null);

/** Access the modal stack. Throws when used outside a LayeredModalHost. */
export function useLayeredModal(): LayeredModalContextValue {
  const ctx = React.useContext(LayeredModalContext);
  if (!ctx) throw new Error("useLayeredModal must be used within <LayeredModalHost>");
  return ctx;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

function LayerOverlay({
  layer,
  isTop,
  isMobile,
  onDismiss,
}: {
  layer: InternalLayer;
  isTop: boolean;
  isMobile: boolean;
  onDismiss: () => void;
}): React.ReactElement {
  const panelRef = React.useRef<HTMLDivElement>(null);
  // The element that had focus when this layer opened. WAI-ARIA dialog: when the
  // layer closes/unmounts, focus MUST return to its opener (not fall back to
  // document.body) so keyboard/screen-reader users keep their place. Captured in a
  // lazy useState initializer so it runs at the FIRST render of this layer — before
  // the panel-focus effect below moves focus into the panel — guaranteeing we grab
  // the real opener and never the panel/its first focusable.
  const [opener] = React.useState<HTMLElement | null>(() =>
    typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null,
  );
  React.useEffect(() => {
    return () => {
      // Guard: opener may be null, the document body (nothing was focused), no
      // longer focusable, or detached from the DOM (its subtree unmounted while the
      // layer was open). Only then restore focus to it.
      if (
        opener &&
        opener !== document.body &&
        typeof opener.focus === "function" &&
        document.contains(opener)
      ) {
        opener.focus();
      }
    };
  }, [opener]);

  // Focus the panel when it becomes the top layer.
  React.useEffect(() => {
    if (!isTop) return;
    const el = panelRef.current;
    if (!el) return;
    const first = el.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? el).focus();
  }, [isTop]);

  const kind: SurfaceKind = isMobile ? "bottom-sheet" : layer.kind;
  const positionClass =
    kind === "bottom-sheet"
      ? "inset-x-0 bottom-0 max-h-[92vh] w-full rounded-t-xl"
      : kind === "sheet"
        ? "inset-y-0 right-0 h-full w-full max-w-md rounded-l-xl"
        : "left-1/2 top-1/2 max-h-[88vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl";

  return (
    <div
      className="fixed inset-0 z-[60] flex"
      style={{ zIndex: 60 + layer.id }}
      data-testid={`surface-layer-${layer.id}`}
      data-surface-kind={kind}
    >
      <div
        className={cn(
          "absolute inset-0 bg-black/40 transition-opacity",
          isTop ? "opacity-100" : "opacity-60",
        )}
        onClick={() => isTop && layer.dismissable && onDismiss()}
        data-testid={isTop ? "surface-backdrop" : undefined}
      />
      <FocusScope
        ref={panelRef}
        trapped
        loop
        // We keep our OWN auto-focus-on-mount (below) and restore-focus-on-unmount
        // (opener effect above) — both already covered by tests — so suppress
        // FocusScope's own default behavior for both and only take its Tab-loop +
        // (crucially) its focus-scope-stack registration/pause-ancestor effect.
        onMountAutoFocus={(e) => e.preventDefault()}
        onUnmountAutoFocus={(e) => e.preventDefault()}
        role="dialog"
        aria-modal={isTop}
        aria-label={layer.title}
        className={cn(
          "absolute flex max-h-full flex-col overflow-hidden border border-border bg-background shadow-xl outline-none",
          positionClass,
          layer.className,
          !isTop && "pointer-events-none",
        )}
      >
        {kind === "bottom-sheet" ? (
          <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-muted-foreground/30" />
        ) : null}
        {layer.title ? (
          <header className="shrink-0 border-b border-border px-4 py-3 text-sm font-semibold">
            {layer.title}
          </header>
        ) : null}
        <div className="min-h-0 flex-1 overflow-auto p-4">
          {layer.render({ close: (result?: unknown) => layer.resolve(result) })}
        </div>
      </FocusScope>
    </div>
  );
}

export interface LayeredModalHostProps {
  children: React.ReactNode;
  /** Max viewport width (px) treated as mobile (bottom-sheet stack). Default 640. */
  mobileBreakpoint?: number;
}

/** Provider + portal host for the layered modal stack. */
export function LayeredModalHost({
  children,
  mobileBreakpoint = 640,
}: LayeredModalHostProps): React.ReactElement {
  const [layers, setLayers] = React.useState<InternalLayer[]>([]);
  const idRef = React.useRef(0);
  const isMobile = useIsMobile(mobileBreakpoint);

  const removeLayer = React.useCallback((id: number, result: unknown) => {
    setLayers((prev) => {
      const layer = prev.find((l) => l.id === id);
      layer?.resolve(result);
      return prev.filter((l) => l.id !== id);
    });
  }, []);

  const open = React.useCallback<LayeredModalContextValue["open"]>(
    (render, options) =>
      new Promise((resolve) => {
        const id = ++idRef.current;
        let settled = false;
        const safeResolve = (result: unknown) => {
          if (settled) return;
          settled = true;
          resolve(result as never);
          setLayers((prev) => prev.filter((l) => l.id !== id));
        };
        const layer: InternalLayer = {
          id,
          kind: options?.kind ?? "modal",
          title: options?.title,
          className: options?.className,
          dismissable: options?.dismissable ?? true,
          render: render as InternalLayer["render"],
          resolve: safeResolve,
        };
        setLayers((prev) => [...prev, layer]);
      }),
    [],
  );

  const closeTop = React.useCallback((result?: unknown) => {
    setLayers((prev) => {
      if (prev.length === 0) return prev;
      const top = prev[prev.length - 1];
      top.resolve(result);
      return prev.slice(0, -1);
    });
  }, []);

  // Esc closes the topmost (dismissable) layer.
  React.useEffect(() => {
    if (layers.length === 0) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const top = layers[layers.length - 1];
      if (top?.dismissable) {
        e.stopPropagation();
        closeTop(undefined);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [layers, closeTop]);

  // Lock body scroll while any layer is open.
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    if (layers.length === 0) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [layers.length]);

  const ctx = React.useMemo<LayeredModalContextValue>(
    () => ({ open, closeTop, depth: layers.length }),
    [open, closeTop, layers.length],
  );

  return (
    <LayeredModalContext.Provider value={ctx}>
      {children}
      {typeof document !== "undefined" &&
        layers.length > 0 &&
        createPortal(
          // TES-2105: a single Branch wraps the whole stack — real pointer-events
          // stay enabled here even when an ancestor Radix modal Dialog disabled
          // them on `document.body`, and Radix's own dismissable-layer machinery
          // treats this subtree as "inside" rather than an outside-click target.
          // The existing per-layer `!isTop && "pointer-events-none"` class (on
          // each panel, see LayerOverlay) still wins for background layers since
          // it's set explicitly on a more specific descendant.
          <DismissableLayerBranch style={{ pointerEvents: "auto" }}>
            {layers.map((layer, i) => (
              <LayerOverlay
                key={layer.id}
                layer={layer}
                isTop={i === layers.length - 1}
                isMobile={isMobile}
                onDismiss={() => removeLayer(layer.id, undefined)}
              />
            ))}
          </DismissableLayerBranch>,
          document.body,
        )}
    </LayeredModalContext.Provider>
  );
}
